import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { addExtraReducer } from '../../../../store/addExtraReducer'
import { LoadingStatusEnum, PeriodRangeString } from '../../../../store/types'
import { SortDirectionCapital } from '../../../../types'
import { AccountFiltersEnum } from '../../Accounts/api/types'
import { BrokerServerFilters, BrokerServerOrderBy } from '../api/types'

import initialState, {
  AdminDashboardCompareShortcut,
  createEmptyCountryDetailsData,
} from './initialState'
import {
  GetAdminBrokerServersReducer,
  GetAdminBreachEventsReducer,
  GetAdminDashboardMetricsReducer,
  GetAdminRegistrationPlatformMetricsReducer,
  GetAdminDashboardMetricsByCountryReducer,
  GetAdminDashboardMetricsByCountryChartReducer,
  GetAdminDashboardTopAccountsReducer,
  SetExcludedFromTopReducer,
  GetTradingLogStatsReducer,
  GetUserQuestionnaireStatsReducer,
  GetPlatformRequestStatsReducer,
  GetTrackedLinkClickCountryStatsReducer,
  GetTrackedLinkClickStatsReducer,
} from './reducers'
import {
  SLICE_NAME,
  GetAdminDashboardMetrics,
  GetAdminRegistrationPlatformMetrics,
  GetAdminDashboardMetricsByCountry,
  GetAdminDashboardMetricsByCountryChart,
  GetAdminBrokerServers,
  GetAdminBreachEvents,
  GetAdminDashboardTopAccounts,
  SetExcludedFromTop,
  SetExcludedFromTopAndRefresh,
  GetTradingLogStats,
  GetUserQuestionnaireStats,
  GetPlatformRequestStats,
  GetTrackedLinkClickCountryStats,
  GetTrackedLinkClickStats,
} from './thunks'

const AdminDashboardSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setPeriod: (state, action: PayloadAction<PeriodRangeString>) => {
      state.period = action.payload
    },
    setQuestionnaireStatsPeriod: (state, action: PayloadAction<PeriodRangeString>) => {
      state.questionnaireStatsPeriod = action.payload
    },
    setLinkClickStatsPeriod: (state, action: PayloadAction<PeriodRangeString>) => {
      state.linkClickStatsPeriod = action.payload
    },
    setActivationFunnelPeriod: (state, action: PayloadAction<PeriodRangeString>) => {
      state.activationFunnelPeriod = action.payload
    },
    setFeatureUsagePeriod: (state, action: PayloadAction<PeriodRangeString>) => {
      state.featureUsagePeriod = action.payload
    },
    setAdminEventsPeriod: (state, action: PayloadAction<PeriodRangeString>) => {
      state.adminEventsPeriod = action.payload
    },
    setChurnSignalsPeriod: (state, action: PayloadAction<PeriodRangeString>) => {
      state.churnSignalsPeriod = action.payload
    },
    setLinkClickSelectedKey: (state, action: PayloadAction<string>) => {
      state.linkClickSelectedKey = action.payload
    },
    setComparePeriod: (
      state,
      action: PayloadAction<{
        period: PeriodRangeString
        shortcut?: AdminDashboardCompareShortcut | null
      }>,
    ) => {
      const { period, shortcut } = action.payload
      state.comparePeriod = period
      state.compareShortcut =
        shortcut !== undefined ? shortcut : period[0] && period[1] ? 'custom' : null
    },
    clearComparePeriod: (state) => {
      state.comparePeriod = [null, null]
      state.compareShortcut = null
    },
    setAccountFilters: (state, action: PayloadAction<AccountFiltersEnum[]>) => {
      state.accountFilters = action.payload
    },
    toggleTopAccountsFilter: (state, action: PayloadAction<AccountFiltersEnum>) => {
      const filter = action.payload
      const filters = state.topAccounts.filters

      state.topAccounts.filters = filters.includes(filter)
        ? filters.filter((f) => f !== filter)
        : [...filters, filter]
    },
    openCountryDetails: (state, action: PayloadAction<string>) => {
      state.countryDetails.country = action.payload
      state.countryDetails.isOpen = true
      state.countryDetails.data = createEmptyCountryDetailsData()
      state.countryDetails.loading = LoadingStatusEnum.Idle
    },
    closeCountryDetails: (state) => {
      state.countryDetails.isOpen = false
      state.countryDetails.country = null
      state.countryDetails.data = createEmptyCountryDetailsData()
      state.countryDetails.loading = LoadingStatusEnum.Idle
    },
    updateBrokerServersPage: (state, action: PayloadAction<number>) => {
      state.brokerServers.pagination.page = action.payload
    },
    updateBrokerServersPageSize: (state, action: PayloadAction<number>) => {
      state.brokerServers.pagination.perPage = action.payload
      state.brokerServers.pagination.page = 1
    },
    updateBrokerServersOrder: (state, action: PayloadAction<SortDirectionCapital>) => {
      state.brokerServers.pagination.order = action.payload
    },
    updateBrokerServersOrderBy: (state, action: PayloadAction<BrokerServerOrderBy | null>) => {
      state.brokerServers.pagination.orderBy = action.payload
    },
    updateBrokerServersSearch: (state, action: PayloadAction<string>) => {
      state.brokerServers.pagination.search = action.payload
      state.brokerServers.pagination.page = 1
    },
    setBrokerServersFilters: (state, action: PayloadAction<BrokerServerFilters>) => {
      state.brokerServers.pagination.filters = action.payload
      state.brokerServers.pagination.page = 1
    },
  },
  extraReducers: (builder) => {
    addExtraReducer(builder, GetAdminDashboardMetrics, GetAdminDashboardMetricsReducer)
    addExtraReducer(builder, GetAdminBreachEvents, GetAdminBreachEventsReducer)
    addExtraReducer(
      builder,
      GetAdminRegistrationPlatformMetrics,
      GetAdminRegistrationPlatformMetricsReducer,
    )
    addExtraReducer(
      builder,
      GetAdminDashboardMetricsByCountry,
      GetAdminDashboardMetricsByCountryReducer,
    )
    addExtraReducer(
      builder,
      GetAdminDashboardMetricsByCountryChart,
      GetAdminDashboardMetricsByCountryChartReducer,
    )
    addExtraReducer(builder, GetAdminBrokerServers, GetAdminBrokerServersReducer)
    addExtraReducer(builder, GetAdminDashboardTopAccounts, GetAdminDashboardTopAccountsReducer)
    addExtraReducer(builder, SetExcludedFromTop, SetExcludedFromTopReducer)
    addExtraReducer(builder, SetExcludedFromTopAndRefresh, GetAdminDashboardTopAccountsReducer)
    addExtraReducer(builder, GetTradingLogStats, GetTradingLogStatsReducer)
    addExtraReducer(builder, GetUserQuestionnaireStats, GetUserQuestionnaireStatsReducer)
    addExtraReducer(builder, GetPlatformRequestStats, GetPlatformRequestStatsReducer)
    addExtraReducer(builder, GetTrackedLinkClickStats, GetTrackedLinkClickStatsReducer)
    addExtraReducer(
      builder,
      GetTrackedLinkClickCountryStats,
      GetTrackedLinkClickCountryStatsReducer,
    )
  },
})

export const {
  setPeriod,
  setQuestionnaireStatsPeriod,
  setLinkClickStatsPeriod,
  setActivationFunnelPeriod,
  setFeatureUsagePeriod,
  setAdminEventsPeriod,
  setChurnSignalsPeriod,
  setLinkClickSelectedKey,
  setComparePeriod,
  clearComparePeriod,
  setAccountFilters,
  toggleTopAccountsFilter,
  openCountryDetails,
  closeCountryDetails,
  updateBrokerServersPage,
  updateBrokerServersPageSize,
  updateBrokerServersOrder,
  updateBrokerServersOrderBy,
  updateBrokerServersSearch,
  setBrokerServersFilters,
} = AdminDashboardSlice.actions

export default AdminDashboardSlice.reducer
